import { z } from 'zod';
import { queryBoolean } from '../api/query-boolean';

export const TENANT_STATUSES = [
  'TRIALING',
  'ACTIVE',
  'PAST_DUE',
  'GRACE_PERIOD',
  'SUSPENDED',
  'CANCELED',
] as const;
export type TenantStatus = (typeof TENANT_STATUSES)[number];

export const MEMBERSHIP_STATUSES = ['INVITED', 'ACTIVE', 'SUSPENDED', 'REMOVED'] as const;
export type TenantMembershipStatus = (typeof MEMBERSHIP_STATUSES)[number];

/** A slug appears in URLs and must be stable, lowercase and collision-free. */
export const tenantSlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(63)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Must be lowercase alphanumeric words separated by hyphens');

export const createTenantRequestSchema = z.object({
  name: z.string().trim().min(1).max(200),
  slug: tenantSlugSchema,
  defaultLocale: z.string().default('en'),
  defaultTimezone: z.string().default('UTC'),
  defaultCurrency: z.string().length(3).default('USD'),
});
export type CreateTenantRequest = z.infer<typeof createTenantRequestSchema>;

export const createOrganizationRequestSchema = z.object({
  name: z.string().trim().min(1).max(200),
  legalName: z.string().trim().max(250).nullish(),
  timezone: z.string().nullish(),
  currencyCode: z.string().length(3).nullish(),
});
export type CreateOrganizationRequest = z.infer<typeof createOrganizationRequestSchema>;

/** Mirrors `personAddressInputSchema`, plus the lat/lng a map picker sets. */
export const tenantAddressInputSchema = z.object({
  line1: z.string().trim().max(200).nullish(),
  line2: z.string().trim().max(200).nullish(),
  city: z.string().trim().max(120).nullish(),
  region: z.string().trim().max(120).nullish(),
  postalCode: z.string().trim().max(20).nullish(),
  /** ISO 3166-1 alpha-2. */
  countryCode: z.string().trim().length(2).nullish(),
  latitude: z.number().min(-90).max(90).nullish(),
  longitude: z.number().min(-180).max(180).nullish(),
});
export type TenantAddressInput = z.infer<typeof tenantAddressInputSchema>;

/**
 * The tenant's editable general information — its "about our church" profile.
 *
 * PATCH semantics, same convention `updatePersonRequestSchema` documents: an
 * omitted key leaves that field unchanged; an explicit `null` on a nullable
 * field clears it. Declared field-by-field rather than `.partial()` so a
 * defaulted field can never silently reset on an unrelated partial save.
 */
export const updateTenantProfileRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    defaultLocale: z.string().trim().min(2).max(35).optional(),
    defaultTimezone: z.string().trim().min(1).max(64).optional(),
    defaultCurrency: z
      .string()
      .trim()
      .toUpperCase()
      .length(3)
      .regex(/^[A-Z]{3}$/, 'Must be a 3-letter currency code')
      .optional(),
    /** The `FileObject` id from a completed upload — null clears the logo. */
    logoFileId: z.uuid().nullish(),
    denominationId: z.uuid().nullish(),
    councilId: z.uuid().nullish(),
    address: tenantAddressInputSchema.nullish(),
  })
  .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'At least one field must be supplied',
  });
export type UpdateTenantProfileRequest = z.infer<typeof updateTenantProfileRequestSchema>;

// --- Platform-wide catalogs (Denominations, Councils) ------------------------

/** Shared shape — a name and whether it still appears in the picker. */
export const createCatalogEntryRequestSchema = z.object({
  name: z.string().trim().min(1).max(150),
  abbreviation: z.string().trim().max(20).nullish(),
});
export type CreateCatalogEntryRequest = z.infer<typeof createCatalogEntryRequestSchema>;

export const updateCatalogEntryRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(150).optional(),
    abbreviation: z.string().trim().max(20).nullish(),
    /** The `FileObject` id from a completed upload — null clears the logo. */
    logoFileId: z.uuid().nullish(),
    isActive: z.boolean().optional(),
  })
  .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'At least one field must be supplied',
  });
export type UpdateCatalogEntryRequest = z.infer<typeof updateCatalogEntryRequestSchema>;

export const listCatalogEntriesQuerySchema = z.object({
  includeArchived: queryBoolean().default(false),
});
export type ListCatalogEntriesQuery = z.infer<typeof listCatalogEntriesQuerySchema>;

/** One entry of the Denomination or Council catalog — same shape for both. */
export interface CatalogEntryResponse {
  readonly id: string;
  readonly name: string;
  readonly abbreviation: string | null;
  readonly logoFileId: string | null;
  readonly isActive: boolean;
  readonly archivedAt: string | null;
}

export const createCampusRequestSchema = z.object({
  organizationId: z.uuid(),
  name: z.string().trim().min(1).max(200),
  code: z.string().trim().min(1).max(50),
  timezone: z.string().nullish(),
});
export type CreateCampusRequest = z.infer<typeof createCampusRequestSchema>;

// --- Pastors ------------------------------------------------------------------

export const createPastorRequestSchema = z.object({
  name: z.string().trim().min(1).max(200),
  roleTitle: z.string().trim().max(150).nullish(),
  phone: z.string().trim().max(40).nullish(),
  email: z.email().trim().max(320).nullish(),
  photoFileId: z.uuid().nullish(),
  /** The other half of a couple, when there is one — must be an existing,
   *  unarchived pastor in the same tenant, and never the pastor itself. */
  spousePastorId: z.uuid().nullish(),
});
export type CreatePastorRequest = z.infer<typeof createPastorRequestSchema>;

export const updatePastorRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    roleTitle: z.string().trim().max(150).nullish(),
    phone: z.string().trim().max(40).nullish(),
    email: z.email().trim().max(320).nullish(),
    photoFileId: z.uuid().nullish(),
    spousePastorId: z.uuid().nullish(),
  })
  .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'At least one field must be supplied',
  });
export type UpdatePastorRequest = z.infer<typeof updatePastorRequestSchema>;

export const listPastorsQuerySchema = z.object({
  includeArchived: queryBoolean().default(false),
});
export type ListPastorsQuery = z.infer<typeof listPastorsQuerySchema>;

export interface PastorResponse {
  readonly id: string;
  readonly name: string;
  readonly roleTitle: string | null;
  readonly phone: string | null;
  readonly email: string | null;
  readonly photoFileId: string | null;
  readonly spousePastorId: string | null;
  readonly archivedAt: string | null;
}

// --- Responses --------------------------------------------------------------

export interface TenantAddressResponse {
  readonly line1: string | null;
  readonly line2: string | null;
  readonly city: string | null;
  readonly region: string | null;
  readonly postalCode: string | null;
  readonly countryCode: string | null;
  readonly latitude: number | null;
  readonly longitude: number | null;
}

export interface TenantResponse {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly status: TenantStatus;
  readonly defaultLocale: string;
  readonly defaultTimezone: string;
  readonly defaultCurrency: string;
  readonly logoFileId: string | null;
  readonly denomination: CatalogEntryResponse | null;
  readonly council: CatalogEntryResponse | null;
  readonly address: TenantAddressResponse | null;
  readonly createdAt: string;
}

export interface OrganizationResponse {
  readonly id: string;
  readonly tenantId: string;
  readonly name: string;
  readonly legalName: string | null;
  readonly timezone: string;
  readonly currencyCode: string;
  readonly archivedAt: string | null;
}

export interface CampusResponse {
  readonly id: string;
  readonly tenantId: string;
  readonly organizationId: string;
  readonly name: string;
  readonly code: string;
  readonly timezone: string;
  readonly archivedAt: string | null;
}

/**
 * The authoritative answer to "what may this user do in this tenant?".
 *
 * Produced by Tenancy + Authorization during token issuance and cached for the
 * life of the access token. It is never assembled from client input.
 */
export interface ResolvedTenantAccess {
  readonly tenantId: string;
  readonly userId: string;
  readonly personId: string | null;
  readonly status: TenantMembershipStatus;
  readonly isOwner: boolean;
  readonly roles: readonly string[];
  readonly permissions: readonly string[];
  readonly organizationIds: readonly string[];
  readonly campusIds: readonly string[];
  readonly unitIds: readonly string[];
  readonly scopes: readonly { scopeType: string; scopeId: string | null }[];
}
